/**
 * RequestsPageV2 — redesigned "User Requests" page.
 *
 * Layout: PageHeader + LibTable. Client-side search filter (the
 * server doesn't support search, same constraint as v1). CSV export
 * and search are header actions. Server-side pagination via the
 * existing fetchRequests action; full LibTable with a pager strip
 * below in the v2 style.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRequests } from '../../../../actions/requestsActions';
import { exportRequestsDataCSV } from '../../../../actions/exportCSVActions';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  DEFAULT_PER_PAGE,
  PARSE_INT_RADIX,
  PER_PAGE_OPTIONS,
} from '../../../../constants/pagination';
import {
  PageHeader,
  Button,
  LibTable,
  cn,
} from '../../../redesign';

const SearchIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const DownloadIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

function formatDateTime(epochSeconds) {
  const dt = new Date(epochSeconds * 1000);
  return {
    date: dt.toLocaleDateString(),
    time: dt.toLocaleTimeString(),
  };
}

export default function RequestsPageV2() {
  const dispatch = useDispatch();
  const { requests, pagination, isLoading } = useSelector((s) => s.requests);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    dispatch(fetchRequests(1, DEFAULT_PER_PAGE));
  }, [dispatch]);

  const filtered = useMemo(() => {
    if (!Array.isArray(requests)) return [];
    if (!searchText) return requests;
    const lc = searchText.toLowerCase();
    return requests.filter(
      (r) =>
        (r.request_message || '').toLowerCase().includes(lc) ||
        (r.user_email || '').toLowerCase().includes(lc) ||
        (r.user_full_name || '').toLowerCase().includes(lc),
    );
  }, [requests, searchText]);

  const handlePrev = () => {
    if (!pagination || pagination.page <= 1) return;
    dispatch(fetchRequests(pagination.page - 1, pagination.per_page));
  };
  const handleNext = () => {
    if (!pagination || pagination.page >= pagination.total_pages) return;
    dispatch(fetchRequests(pagination.page + 1, pagination.per_page));
  };
  const handlePerPage = (e) => {
    dispatch(fetchRequests(1, parseInt(e.target.value, PARSE_INT_RADIX)));
  };

  const handleExport = () => dispatch(exportRequestsDataCSV());

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!isLoading} />
      <PageHeader
        eyebrow="Users"
        title="User Requests"
        total={
          pagination?.total != null
            ? `${pagination.total.toLocaleString()} total`
            : undefined
        }
        actions={
          <Button
            variant="ghost"
            leadingIcon={DownloadIcon}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        }
      />

      <div style={{ padding: '0 28px 32px' }}>
        {/* Search strip */}
        <div
          className="flex items-center"
          style={{ gap: 12, padding: '0 4px 12px' }}
        >
          <div
            className="flex items-center bg-cream border border-sand"
            style={{ borderRadius: 8, padding: '0 12px', gap: 8, flex: 1, maxWidth: 480 }}
          >
            <span className="text-text-muted shrink-0">{SearchIcon}</span>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by message, user, or email…"
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                color: '#2C3E50',
                padding: '8px 0',
              }}
            />
            {searchText && (
              <button
                type="button"
                onClick={() => setSearchText('')}
                className="text-text-muted shrink-0"
                style={{
                  background: 'transparent',
                  border: 0,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  padding: 4,
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <span className="text-text-muted" style={{ fontSize: 11.5 }}>
            {filtered.length.toLocaleString()} shown
          </span>
        </div>

        {filtered.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            {searchText
              ? 'No requests match your search.'
              : isLoading
                ? 'Loading…'
                : 'No requests yet.'}
          </div>
        ) : (
          <>
            <LibTable
              fixed
              className="border border-sand"
              style={{ borderRadius: 12, overflow: 'hidden' }}
            >
              <colgroup>
                {/* ref # */}
                <col style={{ width: 130 }} />
                {/* message */}
                <col />
                {/* user */}
                <col style={{ width: '24%' }} />
                {/* date */}
                <col style={{ width: 160 }} />
              </colgroup>
              <LibTable.Head>
                <tr>
                  <LibTable.Th>Ref. #</LibTable.Th>
                  <LibTable.Th>Message</LibTable.Th>
                  <LibTable.Th>User</LibTable.Th>
                  <LibTable.Th>Date &amp; Time</LibTable.Th>
                </tr>
              </LibTable.Head>
              <tbody>
                {filtered.map((r) => {
                  const epoch = r.timestamp || r.request_epoch;
                  const { date, time } = formatDateTime(epoch);
                  return (
                    <LibTable.Row key={r.id || r.request_id}>
                      <LibTable.Td>
                        <span className="font-mono text-text-muted" style={{ fontSize: 11.5 }}>
                          {r.reference_number || r.request_reference_number || '—'}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        <div
                          className="text-navy"
                          style={{
                            fontSize: 13,
                            lineHeight: 1.4,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {r.request_message || '—'}
                        </div>
                      </LibTable.Td>
                      <LibTable.Td>
                        <div className="text-navy" style={{ fontSize: 13, fontWeight: 600 }}>
                          {r.user_full_name || '—'}
                        </div>
                        <div className="font-mono text-text-muted truncate" style={{ fontSize: 11 }} title={r.user_email}>
                          {r.user_email}
                        </div>
                      </LibTable.Td>
                      <LibTable.Td>
                        <div className="text-navy" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                          {date}
                        </div>
                        <div className="font-mono text-text-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                          {time}
                        </div>
                      </LibTable.Td>
                    </LibTable.Row>
                  );
                })}
              </tbody>
            </LibTable>

            {/* Pager strip */}
            <Pager
              pagination={pagination}
              onPrev={handlePrev}
              onNext={handleNext}
              onPerPage={handlePerPage}
            />
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Pager strip — used by Requests + Feedbacks + similar pages.
 *  Inlined to keep this file self-contained; can extract later if
 *  another v2 page needs the same control. */

export function Pager({ pagination, onPrev, onNext, onPerPage }) {
  if (!pagination) return null;
  const { page = 1, total = 0, per_page = DEFAULT_PER_PAGE, total_pages = 1 } = pagination;
  const from = total === 0 ? 0 : (page - 1) * per_page + 1;
  const to = Math.min(page * per_page, total);
  return (
    <div
      className="flex items-center justify-between"
      style={{
        padding: '12px 14px',
        marginTop: 8,
        borderRadius: 8,
        background: '#FAF7F1',
        border: '1px solid #E8E0CF',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div className="flex items-center" style={{ gap: 8 }}>
        <span
          className="font-condensed font-bold uppercase text-text-muted"
          style={{ fontSize: 10.5, letterSpacing: '0.08em' }}
        >
          Page size
        </span>
        <select
          value={per_page}
          onChange={onPerPage}
          style={{
            fontFamily: 'inherit',
            fontSize: 12.5,
            padding: '4px 8px',
            border: '1px solid #E8E0CF',
            borderRadius: 6,
            background: '#FAF4EA',
            color: '#2C3E50',
          }}
        >
          {PER_PAGE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div className="flex items-center" style={{ gap: 14 }}>
        <span className="font-mono text-text-muted" style={{ fontSize: 12 }}>
          {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
        </span>
        <button
          type="button"
          onClick={onPrev}
          disabled={page <= 1}
          className={cn(
            'font-condensed font-bold uppercase',
          )}
          style={{
            fontSize: 10.5,
            letterSpacing: '0.08em',
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px solid #E8E0CF',
            background: page <= 1 ? '#FAF4EA' : '#FAF7F1',
            color: page <= 1 ? '#9BA4AD' : '#2C3E50',
            cursor: page <= 1 ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          ← Prev
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={page >= total_pages}
          className="font-condensed font-bold uppercase"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.08em',
            padding: '6px 12px',
            borderRadius: 999,
            border: '1px solid #E8E0CF',
            background: page >= total_pages ? '#FAF4EA' : '#FAF7F1',
            color: page >= total_pages ? '#9BA4AD' : '#2C3E50',
            cursor: page >= total_pages ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
